import {
  Box,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { SwapHoriz } from "@mui/icons-material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import TenantMigrationAccordion, {
  TenantMigrationData,
} from "./TenantMigrationAccordion"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const eyebrowSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

type FilterType = "all" | "v1" | "v2"
type SortBy = "tenantName" | "v1Count" | "v2Count" | "totalCount"

export type FleetTotals = {
  v1: number
  v2: number
  total: number
  v2Percentage: number
}

const StatCard: FC<{
  labelKey: string
  suffix?: string
  dotColor: string
  value: number
  loading: boolean
}> = ({ labelKey, suffix, dotColor, value, loading }) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor }}
        />
        <Typography sx={eyebrowSx}>
          {t(labelKey)}
          {suffix ? ` ${suffix}` : ""}
        </Typography>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
        {loading ? <Skeleton width={80} /> : value}
      </Typography>
    </Box>
  )
}

export const Sstv2FleetRollup: FC<{
  migrationData: TenantMigrationData[]
  totals: FleetTotals
  loading: boolean
}> = ({ migrationData, totals, loading }) => {
  const { t } = useTranslation()

  const [searchInput, setSearchInput] = useState("")
  const [filterType, setFilterType] = useState<FilterType>("all")
  const [sortBy, setSortBy] = useState<SortBy>("tenantName")
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null)

  const filteredTenants = useMemo(() => {
    let filtered = migrationData

    if (searchInput) {
      const q = searchInput.toLowerCase()
      filtered = filtered.filter(
        (tenant) =>
          tenant.tenantName.toLowerCase().includes(q) ||
          tenant.lockers.some((locker) =>
            getLockerDisplayValues(locker).i18n.name.toLowerCase().includes(q),
          ),
      )
    }

    if (filterType === "v1") {
      filtered = filtered.filter((tenant) => tenant.v1Count > 0)
    } else if (filterType === "v2") {
      filtered = filtered.filter((tenant) => tenant.v2Count > 0)
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "tenantName":
          return a.tenantName.localeCompare(b.tenantName)
        case "v1Count":
          return b.v1Count - a.v1Count
        case "v2Count":
          return b.v2Count - a.v2Count
        case "totalCount":
          return b.totalCount - a.totalCount
        default:
          return 0
      }
    })
  }, [migrationData, searchInput, filterType, sortBy])

  const handleToggleExpanded = useCallback((tenantId: string) => {
    setExpandedTenant((prev) => (prev === tenantId ? null : tenantId))
  }, [])

  return (
    <div data-ui="UI-SL2W-261" style={{ display: "contents" }}>
      <StatCard
        labelKey="totalLockers"
        dotColor="primary.main"
        value={totals.total}
        loading={loading}
      />
      <StatCard
        labelKey="v1Lockers"
        suffix={`(${loading ? "..." : (100 - totals.v2Percentage).toFixed(0)}%)`}
        dotColor="info.main"
        value={totals.v1}
        loading={loading}
      />
      <StatCard
        labelKey="v2Lockers"
        suffix={`(${loading ? "..." : totals.v2Percentage.toFixed(0)}%)`}
        dotColor="success.main"
        value={totals.v2}
        loading={loading}
      />

      {/* Overall progress section */}
      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <Stack spacing={1}>
          <Typography variant="body2">{t("migrationProgress")}</Typography>
          <LinearProgress
            variant="determinate"
            value={totals.v2Percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "grey.300",
              "& .MuiLinearProgress-bar": { backgroundColor: "success.main" },
            }}
          />
          <Typography variant="caption" textAlign="right">
            {totals.v2Percentage.toFixed(1)}% {t("migrationProgress")}
          </Typography>
        </Stack>
      </Box>

      {/* Search + Filter bar */}
      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
        >
          <TextField
            label={t("search")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            placeholder={`${t("search")} ${t("tenantName")} / ${t("lockerName")}`}
          />
          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(_, newFilter) => newFilter && setFilterType(newFilter)}
            size="small"
          >
            <ToggleButton value="all">{t("all")}</ToggleButton>
            <ToggleButton value="v1">{t("v1Only")}</ToggleButton>
            <ToggleButton value="v2">{t("v2Only")}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            label={t("sortBy")}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="tenantName">{t("tenantName")}</MenuItem>
            <MenuItem value="v1Count">{t("v1Count")}</MenuItem>
            <MenuItem value="v2Count">{t("v2Count")}</MenuItem>
            <MenuItem value="totalCount">{t("totalCount")}</MenuItem>
          </TextField>
        </Stack>
      </Box>

      {loading && (
        <Stack spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Box key={i} sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          ))}
        </Stack>
      )}

      {!loading && (
        <Stack spacing={1}>
          {filteredTenants.map((tenant) => (
            <TenantMigrationAccordion
              key={tenant.tenantId}
              tenant={tenant}
              expanded={expandedTenant === tenant.tenantId}
              onToggle={() => handleToggleExpanded(tenant.tenantId)}
            />
          ))}
        </Stack>
      )}

      {!loading && filteredTenants.length === 0 && (
        <Box textAlign="center" py={4}>
          <SwapHoriz sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t("noLockersMatchFilter")}
          </Typography>
        </Box>
      )}
    </div>
  )
}

export default Sstv2FleetRollup
